import React, { useMemo } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StatusIndicator from '../../../components/ui/StatusIndicator';

const TimelineView = ({ invoices = [], dateBasis, onOpenInvoice, className = '' }) => {
  const grouped = useMemo(() => {
    const map = {};
    for (const inv of invoices) {
      const date = getBasisDate(inv, dateBasis);
      if (!date || !isValid(date)) continue;
      const key = format(date, 'yyyy-MM-dd');
      if (!map[key]) map[key] = [];
      map[key].push(inv);
    }
    return Object.entries(map)
      .map(([key, list]) => ({ key, date: parseISO(key), invoices: list }))
      .sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [invoices, dateBasis]);

  const formatCurrency = (value, currency = 'GBP') => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return '£0.00';
    if (currency === 'USD') return `$${n.toFixed(2)}`;
    if (currency === 'EUR') return `€${n.toFixed(2)}`;
    return `£${n.toFixed(2)}`;
  };

  if (grouped.length === 0) {
    return (
      <div className={`bg-card border border-border rounded-lg p-8 text-center ${className}`}>
        <Icon name="Calendar" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
        <h3 className="text-lg font-medium text-foreground mb-2">No invoices found</h3>
        <p className="text-sm text-muted-foreground">
          Invoices will appear here once they match the selected date basis.
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Timeline</h3>
          <span className="text-sm text-muted-foreground">
            {invoices.length} invoice{invoices.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>
      <div className="p-4 space-y-6 max-h-[640px] overflow-y-auto">
        {grouped.map((group) => {
          const totalGross = group.invoices.reduce((sum, inv) => sum + (inv?.grossAmount || 0), 0);
          const headerRight = dateBasis === 'created_at'
            ? `${group.invoices.length} invoice${group.invoices.length === 1 ? '' : 's'}`
            : formatCurrency(totalGross, group.invoices?.[0]?.currency || 'GBP');

          return (
            <div key={group.key} className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-foreground">
                  {format(group.date, 'EEEE, d MMMM yyyy')}
                </div>
                <div className="text-xs text-muted-foreground">{headerRight}</div>
              </div>

              <div className="space-y-2">
                {group.invoices.map((inv) => {
                  const badgeStatus = inv?.status === 'exported'
                    ? 'reviewed'
                    : inv?.status === 'parsed'
                      ? 'needs_review'
                      : inv?.status;
                  const trustBadge = dateBasis === 'invoice_date'
                    ? inv?.trust?.trusted
                      ? { label: 'Trusted', color: 'text-success' }
                      : { label: 'Untrusted', color: 'text-warning' }
                    : null;

                  return (
                    <div
                      key={inv.id}
                      className="flex items-center justify-between gap-3 bg-background border border-border rounded-lg p-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {inv.vendorName || 'Unknown Vendor'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {inv.invoiceNumber ? `Invoice ${inv.invoiceNumber}` : 'Invoice'}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusIndicator status={badgeStatus} size="sm" />
                        {trustBadge && (
                          <span className={`text-xs font-medium ${trustBadge.color}`}>
                            {trustBadge.label}
                          </span>
                        )}
                        <div className="text-sm font-mono text-foreground min-w-[90px] text-right">
                          {formatCurrency(inv.grossAmount, inv.currency)}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onOpenInvoice?.(inv.id)}
                        >
                          Review
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const getBasisDate = (invoice, dateBasis) => {
  const raw = dateBasis === 'created_at' ? invoice?.createdAt : invoice?.invoiceDate;
  if (!raw) return null;
  const parsed = parseISO(raw);
  return isValid(parsed) ? parsed : null;
};

export default TimelineView;
